import React, { useContext } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  Plan,
  DetailItemProps,
  BenefitItemProps,
  PlanCategoryType,
  PlanDetailsSection as PDS,
  PlanDetailsSection,
} from "../../../@types";
import {
  BenefitItem,
  CollapsibleBox,
  DetailItem as DI,
  ItemIterator,
  PlanDetailList,
  PlanDetailSection,
  PlanDetailListWithHelpDrawer,
} from "../../../components";
import {
  $$,
  makeBenefitItemItems as mBII,
  useTranslate,
  planDataUtils,
  isMapdPlanType,
} from "../../../helpers";
import { AppContext } from "../../../app/store";
import { FormattedMessage } from "react-intl-phraseapp";
import {
  shouldSuppressPlanBenefits,
  calculateYourResponsibility,
  shouldSuppressDrugPricing,
} from "../../../helpers/planHelpers";
import { PreviewContext } from "../../../helpers";

const Overview: React.FunctionComponent<{ plan: Plan }> = ({ plan }) => {
  const {
    oopc: { HEALTH_GOOD: oopc },
  } = plan;

  const makeBenefitItemItems = (
    items: Array<{ label: string | JSX.Element; service?: string }>,
    benefit: string
  ): BenefitItemProps[] => mBII(items, benefit, plan);

  const t = useTranslate();
  const { state } = useContext(AppContext);
  const { mctCurrentPlanYear } = useFlags();
  const previewMode = useContext(PreviewContext);

  const suppressed = shouldSuppressPlanBenefits(plan);
  const DetailItem: React.FunctionComponent<DetailItemProps> = props => {
    const extendedProps = { ...props, suppressed };
    return <DI {...extendedProps} />;
  };

  const isMAPD = isMapdPlanType(plan.plan_type);

  const suppressAnnualDrugEstimate =
    state.prescriptions.length && shouldSuppressDrugPricing(plan);

  const mapdPremiumItems = [
    {
      detail: $$(plan.calculated_monthly_premium),
      label: t("plan_terms.total_monthly_premium"),
    },
    {
      detail: $$(plan.partc_premium),
      label: t("plan_terms.health_plan_premium"),
    },
    {
      detail: $$(
        planDataUtils.getPartDPremiumOrLIS(state, mctCurrentPlanYear, plan)
      ),
      label: t("plan_terms.drug_plan_premium"),
    },
    {
      detail: $$(planDataUtils.getPartBPremium(plan)),
      label: t("plan_terms.partb_premium"),
    },
    {
      detail: plan.partb_premium_reduction > 0 ? t("yes") : t("no"),
      label: t("plan_terms.partb_premium_reduction"),
    },
  ];

  // Exclude drug premium for MA plans
  const maPremiumItems = mapdPremiumItems.filter(
    item => item.label !== t("plan_terms.drug_plan_premium")
  );

  let maDeductibleItems = [
    {
      detail: plan.annual_deductible,
      label: t("plan_terms.health_plan_deductible"),
    },
  ];

  if (plan.category === PlanCategoryType.MSA) {
    maDeductibleItems = [
      ...maDeductibleItems,
      {
        detail: $$(plan.msa_annual_deposit),
        label: t("msa_plan.yearly_deposit"),
      },
      {
        detail: $$(
          calculateYourResponsibility(
            plan.annual_deductible,
            plan.msa_annual_deposit
          )
        ),
        label: t("msa_plan.your_responsibility"),
      },
    ];
  }

  const mapdDeductibleItems = [
    ...maDeductibleItems,
    {
      detail: planDataUtils.getAnnualDrugDeductible(
        state,
        mctCurrentPlanYear,
        plan
      ),
      label: t("plan_terms.drug_plan_deductible"),
    },
  ];

  return (
    <>
      <PlanDetailSection title={t("plan_terms.overview")}>
        <PlanDetailList id={PDS.PREMIUM} title={t("plan_terms.premium")}>
          <ItemIterator Component={DetailItem}>
            {isMAPD ? mapdPremiumItems : maPremiumItems}
          </ItemIterator>
        </PlanDetailList>

        <PlanDetailListWithHelpDrawer
          id={PlanDetailsSection.DEDUCTIBLE}
          titleId="plan_terms.deductible"
          helpId="plan_terms.deductible.definition"
          tealiumProps={{
            event_action: "Plan Details",
            event_label: "Toggle - Deductible",
          }}
        >
          <ItemIterator Component={DetailItem}>
            {isMAPD ? mapdDeductibleItems : maDeductibleItems}
          </ItemIterator>
        </PlanDetailListWithHelpDrawer>

        <PlanDetailList
          id={PDS.ESTIMATED_YEARLY_COSTS}
          title={t("plan_terms.estimated_yearly_costs")}
        >
          <ItemIterator Component={DetailItem}>
            {[
              {
                detail: suppressAnnualDrugEstimate
                  ? t("na")
                  : $$(oopc.annual_breakdown_total),
                label: (
                  <CollapsibleBox
                    labelId="plan_terms.yearly_care_costs"
                    tealiumProps={{
                      event_action: "Plan Details",
                      event_label: "Toggle - estimated total yearly costs",
                    }}
                    open={previewMode}
                  >
                    <FormattedMessage id="plan_terms.yearly_care_costs.definition" />
                  </CollapsibleBox>
                ),
              },
            ]}
          </ItemIterator>
        </PlanDetailList>

        <PlanDetailList id={PDS.MAX_OOPC} title={t("plan_terms.oopc_maximum")}>
          <ItemIterator Component={BenefitItem}>
            {[
              ...makeBenefitItemItems(
                [
                  {
                    label: (
                      <CollapsibleBox
                        labelId="plan_terms.oopc_maximum"
                        tealiumProps={{
                          event_action: "Plan Details",
                          event_label: "Toggle - out-of-pocket max",
                        }}
                        open={previewMode}
                      >
                        <FormattedMessage id="plan_terms.oopc_maximum_definition" />
                      </CollapsibleBox>
                    ),
                  },
                ],
                "maximum_oopc"
              ),
            ]}
          </ItemIterator>
        </PlanDetailList>
      </PlanDetailSection>
    </>
  );
};

export default Overview;
